import { hoardify } from "./Hoard";
import SiteSearch from "./SiteSearch";
import { LanguagePicker } from "../i18n/LanguageContext";
import { STARFLOWER_LOGO } from "./StarPathMark";
import { Fragment, useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Heart, Menu, X, Sparkles, ChevronDown, Bell } from "lucide-react";
import { Button } from "./ui/button";

// Hybrid music-nav: a single "Music Hub ▾" entry that opens to reveal all
// four legacy music routes. All routes still exist on their own URLs.
const SOUNDLIFE_CHILDREN = [
  { to: "/SoundLife",        label: "Music Hub - All Things Music",      testId: "nav-soundlife-hub" },
  { to: "/events/music",     label: "Music Calendar",                    testId: "nav-music-scene" },
  { to: "/artists/featured", label: "Featured Artists",  testId: "nav-featured-artists" },
  { to: "/for-bands",        label: "For Bands",                         testId: "nav-for-bands" },
];

// Time-boxed Porchfest weekend nav item. Auto-hides at 12:00 AM Monday
// (midnight Sunday) of the weekend it advertises. To relight for a future
// weekend, just bump the date below — no other code changes needed.
const PORCHFEST_EXPIRES_AT = new Date("2026-07-20T00:00:00").getTime();
const porchfestActive = () => Date.now() < PORCHFEST_EXPIRES_AT;

// NAV_LINKS rendered in order. The "soundlife" sentinel renders the dropdown
// in-place so we can position it anywhere in the bar (right now: directly
// after Resource Library).
// Desktop row drops the leading emoji so the bar fits one line on laptops; the mobile sheet keeps them.
const deskLabel = (l) => String(l || "").replace(/^[^\p{L}\p{N}]+\s*/u, "");
// "HOliday HOpe HOp" — bold only the HO·HO·HO.
const hohoho = (text) => String(text).split(/(HO(?=[a-z]))/).map((p, i) => (p === "HO" ? <strong key={i} className="font-extrabold">{p}</strong> : <Fragment key={i}>{p}</Fragment>));
const navLabel = (link, label) => (link.hohoho ? hohoho(label) : hoardify(label));

const NAV_LINKS = [
  { to: "/", label: "Home", testId: "nav-home" },
  // Porchfest — pinned directly under HOME, auto-hides Monday 12a via
  // `porchfestActive()` filter below.
  { to: "/porchfest", label: "🎸 Porchfest", testId: "nav-porchfest", color: "#E76F51", bold: true, porchfest: true },
  // Season of HOPE is live (launch_at passed) — the Hop page is the season home.
  { to: "/holiday-hop", label: "🎄 2026 HOliday HOpe HOp", testId: "nav-hope-hop", color: "#1D4ED8", hohoho: true },
  // Season's Community — admin-editable dropdown (Admin → Season's Community nav).
  { type: "season" },
  { to: "/resources", label: "📖 Resources", testId: "nav-resources", color: "#15803D", bold: true, type: "resources" },
  { to: "/community-hub", label: "🗺️ Community Hub", testId: "nav-community-hub", color: "#15803D", bold: true },
  { to: "/SoundLife", label: "🎸 Music Hub", testId: "nav-music-hub", color: "#15803D", bold: true },
  { to: "/events", label: "📅 Calendar", testId: "nav-events" },
  { to: "/map", label: "❤️ Orgs", testId: "nav-map" },
  { to: "/for-businesses", label: "🏪 Business", testId: "nav-business", color: "#2563EB", bold: true },
  // MUSIC HERE dropdown — exposes legacy music sub-routes; lives just
  // after Orgs per latest spec.
  { type: "soundlife" },
  // About dropdown — About, Our Origins, Music & Heart, Outsider's Guide archive.
  { type: "about" },
];

// Admin-editable "Season's Community" menu — items come from /api/nav/season-community.
const useSeasonMenu = () => {
  const [menu, setMenu] = useState({ label: "🌟 Season's Community", items: [] });
  const reload = useCallback(() => { axios.get(`${API}/nav/season-community`).then((r) => setMenu(r.data)).catch(() => {}); }, []);
  useEffect(() => { reload(); }, [reload]);
  return { ...menu, reload };
};

const SeasonDropdown = ({ menu, user, onChanged }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const canReorder = user?.role === "admin" || user?.role === "magician";
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  // Admins & Magicians re-order the menu in place; the full (incl. hidden) list is saved so nothing is lost.
  const move = async (item, dir) => {
    try {
      const { data } = await axios.get(`${API}/admin/nav/season-community`, { withCredentials: true });
      const items = [...(data.items || [])];
      const i = items.findIndex((x) => x.to === item.to);
      let j = i + dir;
      while (j >= 0 && j < items.length && !menu.items.some((v) => v.to === items[j].to)) j += dir;
      if (i < 0 || j < 0 || j >= items.length) return;
      [items[i], items[j]] = [items[j], items[i]];
      await axios.put(`${API}/admin/nav/season-community`, { label: data.label, items }, { withCredentials: true });
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't re-order"); }
  };
  if (!menu.items?.length) return null;
  return (
    <div className="relative" ref={ref} data-testid="nav-season-dropdown">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className="inline-flex items-center gap-1 font-bold text-sm transition-colors hover:opacity-80"
        style={{ color: "#B45309" }}
        data-testid="nav-season-toggle"
      >
        {hoardify(String(menu.label || "").replace(/^[^\p{L}\p{N}]+\s*/u, ""))}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-2 min-w-[280px] bg-white border border-[#E2E0DC] rounded-xl shadow-xl py-2 z-50" data-testid="nav-season-menu">
          {menu.items.map((c, idx) => (
            <div key={c.to} className="flex items-center hover:bg-[#FFF7E6]">
              <Link to={c.to} onClick={() => setOpen(false)} className="flex-1 block px-4 py-2 text-sm text-[#1A1A1A] hover:text-[#B45309]" data-testid={`nav-season-item-${c.to.replace(/\W+/g, "-")}`}>
                {hoardify(c.label)}
              </Link>
              {canReorder && (
                <span className="flex items-center pr-2 text-[#B45309]" data-testid={`nav-season-reorder-${idx}`}>
                  <button type="button" disabled={idx === 0} onClick={() => move(c, -1)} className="p-1 disabled:opacity-20 hover:bg-amber-100 rounded" aria-label="Move up" data-testid={`nav-season-up-${idx}`}><ChevronDown className="h-3.5 w-3.5 rotate-180" /></button>
                  <button type="button" disabled={idx === menu.items.length - 1} onClick={() => move(c, 1)} className="p-1 disabled:opacity-20 hover:bg-amber-100 rounded" aria-label="Move down" data-testid={`nav-season-down-${idx}`}><ChevronDown className="h-3.5 w-3.5" /></button>
                </span>
              )}
            </div>
          ))}
          {canReorder && <p className="px-4 pt-2 text-[10px] text-[#5C5C5C]">{user.role === "magician" ? "Magician" : "Admin"} mode · arrows re-order for everyone</p>}
        </div>
      )}
    </div>
  );
};

const ABOUT_CHILDREN = [
  { to: "/our-origins", label: "Stars 4 Peace — from ORIGINS", testId: "nav-about-origins" },
  { to: "/about", label: "About THE WALL", testId: "nav-about-main" },
  { to: "/music-and-heart-in-wright-park", label: "Music & Heart in Wright Park", testId: "nav-about-mhwp" },
  { to: "/outsiders-guide", label: "Tacoma's Outsider's Guide (2011–2019 archive)", testId: "nav-about-tosg" },
];

const RESOURCES_CHILDREN = [
  { to: "/resources", label: "Resource Library", testId: "nav-resource-library" },
  { to: "/cannabis", label: "Cannabis — a medically informed guide", testId: "nav-cannabis-guide" },
  { to: "/The-Red-Head", label: "The Red Head", testId: "nav-the-red-head" },
  { to: "/safety", label: "Safety — Know the Signs", testId: "nav-safety-guide" },
  { to: "/recovery", label: "Recovery Guide", testId: "nav-recovery-guide" },
  { to: "/abuse", label: "Abuse Guide", testId: "nav-abuse-guide" },
];

const SoundLifeDropdown = ({ onNavigate, label = "MUSIC", items = SOUNDLIFE_CHILDREN, testId = "soundlife" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="relative" ref={ref} data-testid={`nav-${testId}-dropdown`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className="inline-flex items-center gap-1 text-[#5C5C5C] hover:text-[#2E5C55] transition-colors font-medium text-sm"
        data-testid={`nav-${testId}-toggle`}
      >
        {hoardify(label)}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute top-full right-0 mt-2 min-w-[260px] bg-white border border-[#E2E0DC] rounded-xl shadow-xl py-2 z-50"
          data-testid={`nav-${testId}-menu`}
        >
          {items.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              onClick={() => { setOpen(false); onNavigate && onNavigate(); }}
              className="block px-4 py-2 text-sm text-[#1A1A1A] hover:bg-[#F0F7F5] hover:text-[#2E5C55]"
              data-testid={c.testId}
            >
              {hoardify(c.label)}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const timeAgo = (iso) => {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

// Admin-only bell: polls /admin/notifications, shows unread badge + dropdown
const AdminNotifBell = () => {
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () =>
    axios.get(`${API}/admin/notifications/feed`, { withCredentials: true })
      .then((r) => setData({
        notifications: Array.isArray(r.data?.notifications) ? r.data.notifications : [],
        unread_count: r.data?.unread_count || 0,
      }))
      .catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/admin/notifications/read-all`, {}, { withCredentials: true });
      load();
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 text-[#5C5C5C] hover:text-[#2E5C55] transition-colors"
        data-testid="admin-notif-bell"
        aria-label="Admin notifications"
      >
        <Bell className="h-5 w-5" />
        {data.unread_count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 bg-[#E76F51] text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center"
            data-testid="admin-notif-badge"
          >
            {data.unread_count > 99 ? "99+" : data.unread_count}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-[#E2E0DC] rounded-xl shadow-2xl z-[60] overflow-hidden"
          data-testid="admin-notif-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#E2E0DC]">
            <span className="text-sm font-bold text-[#1A1A1A]">Action Items</span>
            {data.unread_count > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#2E5C55] font-semibold hover:underline" data-testid="admin-notif-mark-read">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto overscroll-contain">
            {data.notifications.length === 0 ? (
              <p className="text-sm text-[#5C5C5C] text-center py-8">All caught up — nothing needs you right now.</p>
            ) : (
              data.notifications.map((n) => {
                const inner = (
                  <>
                    <p className={`text-xs leading-snug whitespace-normal break-words ${n.read ? "text-[#5C5C5C]" : "text-[#1A1A1A] font-semibold"}`}>{n.message}</p>
                    <p className="text-[10px] text-[#A8A8A8] mt-0.5">{timeAgo(n.created_at)}</p>
                  </>
                );
                const cls = `block px-4 py-2.5 border-b border-[#F0EEE9] last:border-0 ${n.read ? "" : "bg-[#E76F51]/5"}`;
                return n.link ? (
                  <Link key={n.notif_id} to={n.link} className={`${cls} hover:bg-[#F9F8F6]`} onClick={() => setOpen(false)} data-testid={`admin-notif-item-${n.notif_id}`}>
                    {inner}
                  </Link>
                ) : (
                  <div key={n.notif_id} className={cls} data-testid={`admin-notif-item-${n.notif_id}`}>{inner}</div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const seasonMenu = useSeasonMenu();

  // Lock page scroll while the mobile menu is open — otherwise iOS scrolls the
  // page behind the menu instead of the menu itself
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  // Hide navbar when page is embedded inside an iframe (e.g. /SoundLifeHub)
  if (typeof window !== "undefined" && window.location.search.includes("embed=1")) {
    return null;
  }

  return (
    <nav className="sticky top-0 z-50 glass border-b border-[#E2E0DC] bg-white/80 backdrop-blur" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-4 2xl:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo + search */}
          <div className="flex items-center gap-1 shrink-0">
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="logo-link"
            onClick={(e) => {
              if (window.location.pathname === "/") {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent("wall:open-tour"));
              }
              // If not on home, Link navigates to / and ?tour=1 is not needed — the tour tile is always visible
            }}
            title="THE WALL for YOU — take the tour"
          >
            <Heart className="h-8 w-8 text-[#2E5C55]" fill="#2E5C55" />
            <div className="flex flex-col">
              <span className="font-['Playfair_Display'] text-2xl font-semibold text-[#1A1A1A] leading-tight whitespace-nowrap">
                THE WALL
              </span>
              <span className="text-xs text-[#5C5C5C] whitespace-nowrap hidden sm:block lg:hidden 2xl:block">
                by Artists 4 Life in the Sound
              </span>
            </div>
          </Link>
          <a href="https://mullvad.net/en" target="_blank" rel="noopener noreferrer" title="Protect your safety — use a VPN (Mullvad)" className="ml-1 mr-0.5 shrink-0 rounded-lg overflow-hidden ring-1 ring-[#E2E0DC] hover:ring-[#1D4ED8] transition-shadow" data-testid="nav-mullvad-link">
            <img src="/mullvad.png" alt="Protect your safety — VPN" className="h-9 w-9 object-cover" />
          </a>
          <SiteSearch />
          <LanguagePicker className="ml-0.5 shrink-0" />
          <button
            type="button"
            onClick={() => { if (window.location.pathname !== "/") { window.location.assign("/?tour=1"); } else { window.dispatchEvent(new CustomEvent("wall:open-tour")); } }}
            className="ml-1 hidden md:inline-flex lg:hidden 2xl:inline-flex items-center gap-1.5 rounded-full border border-[#1E3A8A]/30 bg-white px-2.5 py-1 text-[11px] font-bold tracking-wider text-[#1E3A8A] hover:bg-[#F2F6FF] whitespace-nowrap"
            title="THE WALL for YOU — a 60-second tour (which door is yours?)"
            data-testid="nav-start-here-btn"
          >
            <img src={STARFLOWER_LOGO} alt="" className="h-4 w-4 object-contain" /> THE WALL for <u>YOU</u>
          </button>
          <Link
            to="/passport"
            className="ml-1 mr-2 hidden md:flex lg:hidden 2xl:flex flex-col items-center text-center leading-none rounded-full border border-[#54E3FF] bg-[#120A24] px-2.5 py-1 hover:bg-[#1a1033] no-underline whitespace-nowrap"
            title="View The Holiday HOARD — local deals & free music"
            data-testid="nav-starport-link"
          >
            <span className="text-[11px] font-black tracking-wider text-white">View The Holiday <span className="uppercase">HOARD</span></span>
            <span className="text-[9px] font-bold uppercase tracking-wider text-white">Local Deals &amp; Free Music</span>
          </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2.5 2xl:gap-4 whitespace-nowrap text-[13px] 2xl:text-sm [&_a]:!text-[13px] 2xl:[&_a]:!text-sm [&_button]:!text-[13px] 2xl:[&_button]:!text-sm">
            {NAV_LINKS.filter(l => (!l.porchfest || porchfestActive()) && l.to !== "/").map((link, idx) => (
              link.type === "season" ? (
                <SeasonDropdown key={`season-${idx}`} menu={seasonMenu} user={user} onChanged={seasonMenu.reload} />
              ) : link.type === "soundlife" ? (
                <SoundLifeDropdown key={`soundlife-${idx}`} />
              ) : link.type === "about" ? (
                <SoundLifeDropdown key={`about-${idx}`} label="About" items={ABOUT_CHILDREN} testId="about" />
              ) : link.type === "resources" ? (
                <SoundLifeDropdown key={`resources-${idx}`} label="Resources" items={RESOURCES_CHILDREN} testId="resources" />
              ) : link.disabled ? (
                <span
                  key={link.to}
                  className="text-[#A8A8A8] cursor-not-allowed font-medium text-sm select-none"
                  data-testid={link.testId}
                  aria-disabled="true"
                  title={link.title || "Coming Soon"}
                >
                  {hoardify(deskLabel(link.label))}
                </span>
              ) : link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="hover:text-[#2E5C55] transition-colors text-sm font-medium italic"
                  style={{ color: link.color || "#5C5C5C" }}
                  title={link.title}
                  data-testid={link.testId}
                >
                  {hoardify(deskLabel(link.label))}
                </a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`hover:text-[#2E5C55] transition-colors text-sm ${link.bold ? "font-bold" : "font-medium"}`}
                  style={{ color: link.color || "#5C5C5C" }}
                  data-testid={link.testId}
                >
                  {navLabel(link, deskLabel(link.label))}
                </Link>
              )
            ))}
            {user ? (
              <>
                {user.role === "magician" && (
                  <span
                    className="inline-flex items-center gap-1 bg-gradient-to-r from-[#7E5BBE] to-[#2E5C55] text-white text-[11px] font-semibold px-2.5 py-1 rounded-full shadow-sm"
                    data-testid="magician-mode-badge"
                    title="Magician Mode: your submissions auto-publish"
                  >
                    <Sparkles className="h-3 w-3" /> Magician Mode
                  </span>
                )}
                <Link
                  to="/dashboard"
                  className="text-[#5C5C5C] hover:text-[#2E5C55] transition-colors font-medium text-sm"
                  data-testid="nav-dashboard"
                >
                  StarGate
                </Link>
                {user.role === "admin" && (
                  <>
                    <Link
                      to="/admin"
                      className="text-[#5C5C5C] hover:text-[#2E5C55] transition-colors font-medium text-sm"
                      data-testid="nav-admin"
                    >
                      Admin
                    </Link>
                    <AdminNotifBell />
                  </>
                )}
                <button
                  onClick={logout}
                  className="text-[#E76F51] hover:text-[#d35e44] transition-colors font-medium text-sm"
                  data-testid="nav-signout"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <Link to="/auth" data-testid="nav-login">
                <Button variant="outline" className="rounded-full border-[#2E5C55] text-[#2E5C55] hover:bg-[#2E5C55] hover:text-white">
                  Sign In
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile menu button (+ admin bell always visible) */}
          <div className="flex items-center gap-1 lg:hidden">
            {user?.role === "admin" && <AdminNotifBell />}
            <button
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation — scrollable within viewport (sticky nav means the
          menu itself must scroll), Sign In pinned at the top for reachability */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden bg-white border-t border-[#E2E0DC] py-4 px-6 max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-4">
            {user ? (
              <>
                <div className="flex items-center gap-3">
                  <Link to="/dashboard" className="flex-1 text-[#5C5C5C] hover:text-[#2E5C55] font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-dashboard">StarGate</Link>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full border-[#E76F51] text-[#E76F51] hover:bg-[#E76F51] hover:text-white"
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    data-testid="mobile-nav-signout"
                  >
                    Sign Out
                  </Button>
                </div>
                {user.role === "admin" && (
                  <Link to="/admin" className="text-[#5C5C5C] hover:text-[#2E5C55] font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-admin">Admin</Link>
                )}
              </>
            ) : (
              <Link to="/auth" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-login">
                <Button className="w-full bg-[#2E5C55] hover:bg-[#2E5C55]/90 rounded-full">Sign In</Button>
              </Link>
            )}
            {NAV_LINKS.filter(l => !l.porchfest || porchfestActive()).map((link, idx) => (
              link.type === "season" ? (
                seasonMenu.items?.length ? (
                  <div key={`season-${idx}`} className="border-y border-dashed border-[#E2E0DC] py-3" data-testid="nav-season-mobile">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-[#B45309] mb-2">{seasonMenu.label}</p>
                    {seasonMenu.items.map((c) => (
                      <Link key={c.to} to={c.to} onClick={() => setMobileMenuOpen(false)} className="block py-1.5 text-sm text-[#1A1A1A] hover:text-[#B45309]">
                        {hoardify(c.label)}
                      </Link>
                    ))}
                  </div>
                ) : null
              ) : link.type === "soundlife" || link.type === "about" || link.type === "resources" ? (
                <div key={`${link.type}-${idx}`} className="border-y border-dashed border-[#E2E0DC] py-3" data-testid={`nav-${link.type}-mobile`}>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-[#5C5C5C] mb-2">{link.type === "about" ? "ABOUT" : link.type === "resources" ? "RESOURCES" : "MUSIC HERE"}</p>
                  {(link.type === "about" ? ABOUT_CHILDREN : link.type === "resources" ? RESOURCES_CHILDREN : SOUNDLIFE_CHILDREN).map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-1.5 text-sm text-[#1A1A1A] hover:text-[#2E5C55]"
                    >
                      {hoardify(c.label)}
                    </Link>
                  ))}
                </div>
              ) : link.disabled ? (
                <span
                  key={link.to}
                  className="text-[#A8A8A8] cursor-not-allowed select-none"
                  data-testid={`${link.testId}-mobile`}
                  aria-disabled="true"
                >
                  {hoardify(link.label)}
                </span>
              ) : link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="hover:text-[#2E5C55] italic"
                  style={{ color: link.color || "#5C5C5C" }}
                  data-testid={`${link.testId}-mobile`}
                >
                  {hoardify(link.label)}
                </a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`hover:text-[#2E5C55] ${link.bold ? "font-bold" : ""}`}
                  style={{ color: link.color || "#5C5C5C" }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {navLabel(link, link.label)}
                </Link>
              )
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
